import { useCallback, useEffect, useRef, useState } from 'react'

import { debounce } from '@/shared/utils'

interface ScrollHookEvent {
  scroll: {
    scrollY: number
  }
  reach: {
    targetId: string
  }
}
export interface Target {
  element: HTMLElement | null
  margin?: number
}
export interface ScrollAction {
  isInit: boolean
  initScroll: (scrollElement?: HTMLElement | null) => void
  getScrollY: () => number | undefined
  getAllTarget: () => ({ id: string } & Target)[]
  getTargetById: (id: string) => Target | undefined
  addTarget: (id: string, target: Target) => void
  updateTargetMargin: (id: string, margin: number) => void
  removeTargetById: (id: string) => void
  scrollTo: (y: number) => void
  scrollToTarget: (id: string, margin?: number) => void
  subscribe: <K extends keyof ScrollHookEvent>(eventName: K, listener: (event: ScrollHookEvent[K]) => void) => void
  unsubscribe: <K extends keyof ScrollHookEvent>(eventName: K, listener: (event: ScrollHookEvent[K]) => void) => void
}

export const useScroll = (): ScrollAction => {
  const [isInit, setIsInit] = useState(false)
  const [scrollElement, setScrollElement] = useState<HTMLElement>()
  const scrollListenersRef = useRef<((event: ScrollHookEvent['scroll']) => void)[]>([])
  const reachListenersRef = useRef<((event: ScrollHookEvent['reach']) => void)[]>([])
  const targetsRef = useRef<Map<string, Target>>(new Map())
  const requestIds = useRef<number[]>([])
  const fps = useRef<number>(0)
  const lastY = useRef<number>()
  const frameCount = useRef<number>(0)
  const isScrolling = useRef(false)

  const getScrollElementMargin = useCallback(() => {
    if (!scrollElement) {
      return 0
    }
    const style = window.getComputedStyle(scrollElement)
    const marginTop = parseInt(style.marginTop.replace(/[\D]+/, '').replace(/^$/, '0'), 10)
    const paddingTop = parseInt(style.paddingTop.replace(/[\D]+/, '').replace(/^$/, '0'), 10)
    return marginTop + paddingTop
  }, [scrollElement])

  const cancelScrolling = useCallback(() => {
    requestIds.current.forEach((requestId) => cancelAnimationFrame(requestId))
    requestIds.current = []
  }, [])

  const scrollTo = useCallback(
    (y: number, time = 500) => {
      if (!scrollElement) {
        return
      }

      cancelScrolling()

      fps.current = 0
      frameCount.current = 0
      lastY.current = y
      isScrolling.current = true

      const ONE_SECOND = 1000 // 1s == 1000ms
      const startTime = performance.now()
      const fromY = scrollElement.scrollTop
      const toY = y - fromY
      const d = y - fromY >= 0 ? 1 : -1

      const frame = (now: number) => {
        frameCount.current++

        const scrollingTime = now - startTime
        const scrollingY = scrollElement.scrollTop - fromY

        fps.current = (ONE_SECOND / scrollingTime) * frameCount.current
        // 순간 이동할 y는 남은 이동Y(toY - scrollingY)를 남은시간(단위:s) * fps로 나눈다.
        const dY = (toY - scrollingY) / ((fps.current * (time - scrollingTime)) / ONE_SECOND)

        if (scrollingTime >= time || (scrollElement.scrollTop + dY - y) * d > 0) {
          scrollElement.scrollTop = y
          isScrolling.current = false
          cancelScrolling()
        } else {
          scrollElement.scrollTop += dY
          if (lastY.current === y) {
            requestIds.current.push(requestAnimationFrame(frame))
          }
        }
      }
      requestIds.current.push(requestAnimationFrame(frame))
    },
    [scrollElement, cancelScrolling],
  )

  const getAbsoluteTop = useCallback((element: HTMLElement, scrollTop: number) => {
    return scrollTop + Math.floor(element.getBoundingClientRect().top)
  }, [])

  useEffect(() => {
    if (!scrollElement) {
      return
    }

    let lastTargetId = ''
    let prevTargetId = ''
    let prevTargetTop = 0

    const onScroll = debounce(() => {
      const scrollY = scrollElement.scrollTop

      scrollListenersRef.current.map((onScroll) => {
        onScroll({ scrollY })
      })

      prevTargetId = ''
      prevTargetTop = 0

      targetsRef.current.forEach((target, targetId) => {
        // toScroll 함수로 이동중이거나, 현재타겟의 엘리먼트가 없으면 종료
        if (isScrolling.current || !target.element) {
          return
        }

        // 스크롤엘리먼트 안에 있는 현재타겟의 절대높이값 가져오기
        const targetTop = getAbsoluteTop(target.element, scrollY)
        const margin = target?.margin ? target.margin : 0

        // 스크롤 위치가 현재타겟의 위치를 지났을 경우
        if (scrollY >= targetTop + margin) {
          prevTargetId = targetId
          prevTargetTop = targetTop

          // 이전타겟의 위치보다 현재타겟 위치가 더 밑에 있고, 이전타겟과 마지막타겟이 다를 경우
        } else if (prevTargetTop < targetTop + margin && prevTargetId !== lastTargetId) {
          lastTargetId = prevTargetId
          reachListenersRef.current.map((onReach) => {
            onReach({ targetId: lastTargetId })
          })
        }
      })

      // 이전타겟이 존재했고, 이전타겟과 마지막 타겟이 다를 경우
      // (targets의 마지막 값이 범위 안에 들어왔는지 체크)
      if (prevTargetTop && prevTargetId !== lastTargetId) {
        lastTargetId = prevTargetId
        reachListenersRef.current.map((onReach) => {
          onReach({ targetId: lastTargetId })
        })
      }
    }, 10)

    scrollElement === document.documentElement
      ? document.addEventListener('scroll', onScroll)
      : scrollElement?.addEventListener('scroll', onScroll)

    return () => {
      scrollElement === document.documentElement
        ? document.removeEventListener('scroll', onScroll)
        : scrollElement?.removeEventListener('scroll', onScroll)
    }
  }, [scrollElement, getAbsoluteTop])

  return {
    isInit,
    initScroll(scrollElement?: HTMLElement | null) {
      if (scrollElement) {
        setScrollElement(scrollElement)
      } else {
        setScrollElement(document.documentElement)
      }
      setIsInit(true)
    },

    getScrollY() {
      return scrollElement?.scrollTop
    },

    getAllTarget() {
      const allTarget: ({ id: string } & Target)[] = []
      targetsRef.current.forEach((target, id) => {
        allTarget.push({ id, ...target })
      })
      return allTarget
    },

    getTargetById(id: string) {
      return targetsRef.current.get(id)
    },

    addTarget(id: string, target: Target) {
      target.margin = !target.margin ? 0 : target.margin
      // Map 에서 target의 위치값을 정렬하기 위해 배열에 먼저 담기
      const targets: [string, Target][] = [[id, target]]
      // 기존 Map에 담긴 target들을 배열에 넣기
      targetsRef.current.forEach((value, key) => targets.push([key, value]))
      // target의 Top 값으로 오름차순 정렬하기
      targets.sort((a, b) =>
        !a[1].element || !b[1].element ? -1 : getAbsoluteTop(a[1].element, 0) - getAbsoluteTop(b[1].element, 0),
      )
      targetsRef.current = new Map(targets)
    },

    updateTargetMargin(id: string, margin: number) {
      const prevTarget = targetsRef.current.get(id)

      if (!prevTarget || margin === prevTarget.margin) {
        return
      }

      const target: Target = {
        element: prevTarget.element,
        margin,
      }

      targetsRef.current.set(id, target)
    },

    removeTargetById(id: string): void {
      const beforeRemoveTargets = new Map(targetsRef.current)
      beforeRemoveTargets.delete(id)
      targetsRef.current = beforeRemoveTargets
    },

    scrollTo,

    scrollToTarget(id: string, margin = 0) {
      const target = targetsRef.current.get(id)
      const extraMargin = margin + (target?.margin ? target.margin : 0)
      if (!scrollElement || !target?.element) {
        return
      }

      scrollElement.scrollBy({
        top:
          getAbsoluteTop(target.element, scrollElement.scrollTop) +
          extraMargin -
          getScrollElementMargin() -
          scrollElement.scrollTop,
        behavior: 'smooth',
      })
    },

    subscribe<K extends keyof ScrollHookEvent>(eventName: K, listener: (event: ScrollHookEvent[K]) => void) {
      switch (eventName) {
        case 'scroll':
          const onScroll = listener as (event: ScrollHookEvent['scroll']) => void
          scrollListenersRef.current.push(onScroll)
          return
        case 'reach':
          const onReach = listener as (event: ScrollHookEvent['reach']) => void
          reachListenersRef.current.push(onReach)
          return
      }
    },

    unsubscribe<K extends keyof ScrollHookEvent>(eventName: K, listener: (event: ScrollHookEvent[K]) => void) {
      switch (eventName) {
        case 'scroll':
          const onScroll = listener as (event: ScrollHookEvent['scroll']) => void
          scrollListenersRef.current = scrollListenersRef.current.filter((fn) => fn !== onScroll)
          return
        case 'reach':
          const onReach = listener as (event: ScrollHookEvent['reach']) => void
          reachListenersRef.current = reachListenersRef.current.filter((fn) => fn !== onReach)
          return
      }
    },
  }
}
